import { supabase } from '@config/supabase';
import { AudioFile } from '@types';

const STORAGE_API_URL = (import.meta.env.VITE_STORAGE_API_URL || '').replace(/\/$/, '');

interface CachedUrl {
  url: string;
  expiresAt: number;
}

export class StorageApiService {
  storageBaseUrl: string | null = null;
  urlCache = new Map<string, CachedUrl>(); // Cache key: `${userId}:${s3Key}`

  constructor() {
    if (STORAGE_API_URL) {
      this.storageBaseUrl = STORAGE_API_URL;
      console.log('✅ Storage API configured:', this.storageBaseUrl);
    } else {
      console.warn('⚠️ Storage API not configured. Set VITE_STORAGE_API_URL to enable cloud storage.');
    }
    
    // Clean up expired cache entries every 5 minutes
    setInterval(() => {
      const now = Date.now();
      for (const [key, cached] of this.urlCache.entries()) {
        if (cached.expiresAt < now) {
          this.urlCache.delete(key);
        }
      }
    }, 5 * 60 * 1000);
  }

  getCacheKey(userId: string, s3Key: string): string {
    return `${userId}:${s3Key}`;
  }

  getCachedUrl(userId: string, s3Key: string): string | null {
    const key = this.getCacheKey(userId, s3Key);
    const cached = this.urlCache.get(key);
    
    if (cached && cached.expiresAt > Date.now()) {
      console.log('⚡ [Storage] Using cached signed URL for:', s3Key.substring(0, 50));
      return cached.url;
    }
    
    if (cached) {
      this.urlCache.delete(key);
    }
    
    return null;
  }

  setCachedUrl(userId: string, s3Key: string, url: string, expiresIn: number = 3600): void {
    const key = this.getCacheKey(userId, s3Key);
    // Cache for 95% of expiry time (URLs are valid for 24h, cache for ~23h)
    // This gives a 5% buffer before expiry while maximizing cache hits
    const cacheExpiry = Date.now() + (expiresIn * 1000 * 0.95);
    this.urlCache.set(key, { url, expiresAt: cacheExpiry });
    console.log('💾 [Storage] Cached signed URL for:', s3Key.substring(0, 50));
  }

  isConfigured(): boolean {
    return this.storageBaseUrl !== null;
  }

  /**
   * Upload file via Storage API (Hetzner)
   */
  async uploadToStorage(
    file: File,
    s3Key: string,
    userId: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    if (!this.storageBaseUrl) {
      throw new Error('Storage API is not configured. Please set VITE_STORAGE_API_URL.');
    }

    try {
      console.log('📤 [Storage] Requesting upload URL for key:', s3Key);

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token ?? null;

      const response = await fetch(`${this.storageBaseUrl}/storage/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify({
          objectKey: s3Key,
          bytes: file.size,
          contentType: file.type || 'application/octet-stream',
          userId
        })
      });

      if (response.status === 402) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || 'Storage or bandwidth limit reached';
        const error = new Error(message);
        (error as any).isStorageLimitError = true;
        (error as any).status = 402;
        throw error;
      }

      if (!response.ok) {
        const payload = await response.text();
        const error = new Error(`Failed to request upload URL (${response.status}): ${payload}`);
        (error as any).status = response.status;
        if (!response.ok && response.status === 0) {
          (error as any).isNetworkError = true;
        }
        throw error;
      }

      const { data } = await response.json();
      if (!data?.uploadUrl) {
        throw new Error('Storage API did not return an upload URL');
      }

      console.log('☁️ [Storage] Uploading directly to Hetzner Object Storage');

      onProgress?.(40);

      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        const payload = await uploadResponse.text();
        const error = new Error(`Hetzner upload failed (${uploadResponse.status}): ${payload}`);
        (error as any).status = uploadResponse.status;
        if (uploadResponse.status === 0) {
          (error as any).isNetworkError = true;
        }
        throw error;
      }

      onProgress?.(100);

      console.log('✅ [Storage] File uploaded successfully:', s3Key);
    } catch (error: any) {
      console.error('❌ [Storage] Upload failed:', error);
      
      const uploadError = new Error(error?.message || 'Storage upload failed');
      if (error?.isStorageLimitError) {
        (uploadError as any).isStorageLimitError = true;
      }
      if (error?.isNetworkError) {
        (uploadError as any).isNetworkError = true;
      }
      if (error?.status) {
        (uploadError as any).status = error.status;
      }
      
      throw uploadError;
    }
  }

  /**
   * Delete file from storage
   */
  async deleteFromStorage(userId: string, s3Key: string): Promise<void> {
    if (!this.storageBaseUrl) {
      console.warn('⚠️ Storage API not configured, cannot delete file');
      return;
    }

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token ?? null;

      const response = await fetch(`${this.storageBaseUrl}/storage/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify({
          objectKey: s3Key,
          userId
        })
      });

      if (!response.ok) {
        const payload = await response.text();
        throw new Error(`Failed to delete object (${response.status}): ${payload}`);
      }

      // Invalidate URL cache when file is deleted
      const cacheKey = this.getCacheKey(userId, s3Key);
      if (this.urlCache.has(cacheKey)) {
        this.urlCache.delete(cacheKey);
        console.log('🗑️ [Storage] Invalidated URL cache for deleted file:', s3Key.substring(0, 50));
      }

      console.log('✅ [Storage] File deleted successfully:', s3Key);
    } catch (error) {
      console.error('❌ [Storage] Delete failed:', error);
    }
  }

  /**
   * Get signed URL for storage object (for playback/download)
   */
  async getSignedUrl(s3Key: string, ownerId?: string, allowPublicAccess: boolean = false, bypassCache: boolean = false): Promise<string> {
    if (!this.storageBaseUrl) {
      throw new Error('Storage API is not configured');
    }

    let userId: string | undefined = ownerId;

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token ?? null;

      if (!userId) {
        const user = session.data.session?.user ?? (await supabase.auth.getUser()).data.user;
        if (!user) {
          if (allowPublicAccess && ownerId) {
            userId = ownerId;
          } else {
            throw new Error('Unable to resolve user for download URL');
          }
        } else {
          userId = user.id;
        }
      }

      if (!bypassCache) {
        const cachedUrl = this.getCachedUrl(userId, s3Key);
        if (cachedUrl) {
          return cachedUrl;
        }
      } else {
        console.log('🔄 [Storage] Bypassing URL cache for fresh signed URL (s3Key:', s3Key.substring(0, 50), '...)');
      }

      let endpoint = '/storage/download-url';
      if (this.storageBaseUrl && !this.storageBaseUrl.includes('/api')) {
        endpoint = '/api/storage/download-url';
      }
      
      const apiUrl = `${this.storageBaseUrl}${endpoint}`;
      
      console.log('🔗 [Storage] Requesting signed URL:', {
        apiUrl: apiUrl.substring(0, 100),
        s3Key: s3Key.substring(0, 50),
        userId: userId?.substring(0, 20)
      });

      const shouldSendAuth = !(allowPublicAccess && ownerId);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(shouldSendAuth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify({
          objectKey: s3Key,
          userId
        })
      });

      if (response.status === 404) {
        console.error('❌ [Storage] File not found in storage:', {
          s3Key: s3Key,
          userId: userId?.substring(0, 20),
          status: response.status,
          apiUrl: apiUrl
        });
        throw new Error(`File not found in storage: ${s3Key}`);
      }

      if (response.status === 402) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Bandwidth limit reached');
      }

      if (!response.ok) {
        let payload: string;
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          try {
            const jsonPayload = await response.json();
            payload = typeof jsonPayload === 'string' ? jsonPayload : JSON.stringify(jsonPayload);
          } catch {
            const clonedResponse = response.clone();
            payload = await clonedResponse.text();
          }
        } else {
          payload = await response.text();
        }
        throw new Error(`Failed to generate download URL (${response.status}): ${payload}`);
      }

      const { data } = await response.json();
      if (!data?.downloadUrl) {
        throw new Error('Storage API did not return a download URL');
      }

      if (!bypassCache) {
        this.setCachedUrl(userId, s3Key, data.downloadUrl, data.expiresIn || 3600);
      }

      return data.downloadUrl;
    } catch (error: any) {
      console.error('❌ Failed to generate signed URL:', {
        error,
        message: error?.message,
        s3Key: s3Key?.substring(0, 50),
        userId: userId?.substring(0, 20)
      });
      
      let errorMessage = 'Failed to generate signed URL';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Batch get signed URLs for multiple storage objects
   */
  async getSignedUrlsBatch(s3Keys: string[], ownerId?: string): Promise<Map<string, string>> {
    if (!this.storageBaseUrl) {
      console.warn('⚠️ Storage API not configured, skipping batch URL pre-loading');
      return new Map();
    }

    if (s3Keys.length === 0) {
      return new Map();
    }

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token ?? null;

      let userId = ownerId;
      if (!userId) {
        const user = session.data.session?.user ?? (await supabase.auth.getUser()).data.user;
        if (!user) {
          console.warn('⚠️ No user found for batch URL pre-loading');
          return new Map();
        }
        userId = user.id;
      }

      const uncachedKeys: string[] = [];
      const cachedUrls = new Map<string, string>();

      for (const s3Key of s3Keys) {
        const cached = this.getCachedUrl(userId, s3Key);
        if (cached) {
          cachedUrls.set(s3Key, cached);
        } else {
          uncachedKeys.push(s3Key);
        }
      }

      if (uncachedKeys.length === 0) {
        return cachedUrls;
      }

      const batchSize = 50;
      const resultMap = new Map<string, string>(cachedUrls);

      for (let i = 0; i < uncachedKeys.length; i += batchSize) {
        const batch = uncachedKeys.slice(i, i + batchSize);
        
        try {
          const response = await fetch(`${this.storageBaseUrl}/storage/batch-download-urls`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
            },
            body: JSON.stringify({
              objectKeys: batch,
              userId
            })
          });

          if (!response.ok) {
            console.warn(`⚠️ Batch URL request failed (${response.status}), continuing with cached URLs`);
            continue;
          }

          const { data } = await response.json();
          
          if (data?.urls) {
            for (const urlData of data.urls) {
              this.setCachedUrl(userId, urlData.objectKey, urlData.downloadUrl, urlData.expiresIn || 3600);
              resultMap.set(urlData.objectKey, urlData.downloadUrl);
            }
          }
        } catch (error: any) {
          console.warn('⚠️ Batch URL pre-loading failed for batch, continuing:', error?.message);
        }
      }

      return resultMap;
    } catch (error: any) {
      console.warn('⚠️ Batch URL pre-loading failed, continuing without pre-loaded URLs:', error?.message);
      return new Map();
    }
  }

  /**
   * Pre-load signed URLs for multiple files
   */
  async preloadSignedUrls(files: AudioFile[], maxFiles: number = 60): Promise<void> {
    if (!this.storageBaseUrl) return;

    const filesToPreload = files
      .filter(file => 
        file.s3_key && 
        !file.cdn_url && 
        file.userId &&
        !this.getCachedUrl(file.userId, file.s3_key)
      )
      .slice(0, maxFiles);

    if (filesToPreload.length === 0) return;

    console.log(`⚡ [Storage] Pre-loading URLs for ${filesToPreload.length} files...`);

    const filesByUser = new Map<string, AudioFile[]>();
    for (const file of filesToPreload) {
      if (!file.userId) continue;
      const userFiles = filesByUser.get(file.userId) || [];
      userFiles.push(file);
      filesByUser.set(file.userId, userFiles);
    }

    const preloadPromises = Array.from(filesByUser.entries()).map(async ([userId, userFiles]) => {
      try {
        const s3Keys = userFiles.map(f => f.s3_key!).filter(Boolean);
        if (s3Keys.length > 0) {
          await Promise.race([
            this.getSignedUrlsBatch(s3Keys, userId),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('URL preload timeout')), 8000)
            )
          ]);
        }
      } catch (error) {
        console.warn(`⚠️ [Storage] Failed to preload URLs for user ${userId}:`, error);
      }
    });

    await Promise.allSettled(preloadPromises);
    console.log(`✅ [Storage] URL pre-loading completed`);

    const prefetchCount = 3;
    const PREFETCH_BYTES = 262144;
    for (const file of filesToPreload.slice(0, prefetchCount)) {
      if (!file.userId || !file.s3_key) continue;
      const url = this.getCachedUrl(file.userId, file.s3_key);
      if (!url) continue;
      fetch(url, {
        method: 'GET',
        headers: { Range: `bytes=0-${PREFETCH_BYTES - 1}` },
        keepalive: true
      })
        .then(() => {})
        .catch(() => {});
    }
  }
}

export const storageApiService = new StorageApiService();
export default storageApiService;
